/**
 * Events — typed pub/sub. One process-wide `bus` so systems can talk without holding
 * references to each other; dispatch is re-entrancy safe and allocation free.
 */

export interface GameEvents {
  'mode:change': { mode: 'swim' | 'lab' };
  'lab:regenerate': { seed: number };
  'lab:species': { id: string };
  'shark:meshed': { id: string; triangles: number; ms: number };
  'player:lunge': { x: number; z: number; heading: number };
  'debug:toggle': { open: boolean };
}

/** `never` is assignable from any payload type, so one slot type stores every handler. */
type Erased = (payload: never) => void;

interface Entry {
  fn: Erased;
  once: boolean;
}

/**
 * `M extends Record<keyof M, unknown>` rather than `Record<string, unknown>`: TypeScript
 * refuses to give an `interface` an implicit index signature, so the documented constraint
 * would reject `GameEvents` outright. This form accepts it and keeps `keyof M` exact.
 */
export class EventBus<M extends Record<keyof M, unknown>> {
  /** null slots are tombstones left by an unsubscribe that happened mid-dispatch */
  private readonly lists = new Map<keyof M, Array<Entry | null>>();
  private depth = 0;
  private dirty = false;

  on<K extends keyof M>(key: K, fn: (payload: M[K]) => void): () => void {
    let list = this.lists.get(key);
    if (list === undefined) {
      list = [];
      this.lists.set(key, list);
    }
    list.push({ fn: fn as Erased, once: false });
    return () => this.off(key, fn);
  }

  once<K extends keyof M>(key: K, fn: (payload: M[K]) => void): () => void {
    let list = this.lists.get(key);
    if (list === undefined) {
      list = [];
      this.lists.set(key, list);
    }
    list.push({ fn: fn as Erased, once: true });
    return () => this.off(key, fn);
  }

  off<K extends keyof M>(key: K, fn: (payload: M[K]) => void): void {
    const list = this.lists.get(key);
    if (list === undefined) return;
    const target = fn as Erased;
    for (let i = 0; i < list.length; i++) {
      const e = list[i];
      if (e === null || e.fn !== target) continue;
      if (this.depth > 0) {
        list[i] = null;
        this.dirty = true;
      } else {
        list.splice(i, 1);
      }
      return;
    }
  }

  emit<K extends keyof M>(key: K, payload: M[K]): void {
    const list = this.lists.get(key);
    if (list === undefined) return;
    // Snapshot the length, not the array: handlers subscribed during this dispatch wait
    // for the next emit, and handlers removed during it are skipped via their tombstone.
    const n = list.length;
    if (n === 0) return;
    this.depth++;
    try {
      for (let i = 0; i < n; i++) {
        const e = list[i];
        if (e === null) continue;
        if (e.once) {
          list[i] = null;
          this.dirty = true;
        }
        e.fn(payload as never);
      }
    } finally {
      this.depth--;
      if (this.depth === 0 && this.dirty) this.compact();
    }
  }

  clear(): void {
    // blank in place first — an emit further up the stack is still walking these arrays
    for (const list of this.lists.values()) list.fill(null);
    this.lists.clear();
    this.dirty = false;
  }

  private compact(): void {
    for (const [key, list] of this.lists) {
      let w = 0;
      for (let i = 0; i < list.length; i++) {
        const e = list[i];
        if (e !== null) list[w++] = e;
      }
      list.length = w;
      if (w === 0) this.lists.delete(key);
    }
    this.dirty = false;
  }
}

export const bus = new EventBus<GameEvents>();
